import {
  CASHBOOK_DEBT_PAYMENT_CATEGORY,
  DEFAULT_CASHBOOK_CATEGORIES,
  DEFAULT_CASHBOOK_DEBT_CATEGORIES,
  PAYVOX_DAILY_TOTAL_MEMO_AR,
  PAYVOX_DAILY_TOTAL_MEMO_EN
} from '@finvox/shared/cashbook';
import type { TFunction } from 'i18next';

const CASH_CATEGORY_KEYS = new Set<string>(DEFAULT_CASHBOOK_CATEGORIES.map((c) => c.key));
const DEBT_CATEGORY_KEYS = new Set<string>(DEFAULT_CASHBOOK_DEBT_CATEGORIES.map((c) => c.key));

/** Legacy memos stored as English display titles (imports / older rows). */
const LEGACY_EN_CASH_LABEL_TO_KEY: Record<string, string> = {
  Sales: 'sales',
  'Other income': 'other_income',
  Rent: 'rent',
  Utilities: 'utilities',
  Salaries: 'salaries',
  Supplies: 'supplies',
  'Other expense': 'other_expense',
  'Debt payment': CASHBOOK_DEBT_PAYMENT_CATEGORY
};

const LEGACY_EN_DEBT_LABEL_TO_KEY: Record<string, string> = {
  'Supplier credit': 'supplier',
  'Borrowed money': 'loan',
  'Other debt': 'other'
};

function resolveStoredCategoryKey(label: string): string | null {
  if (CASH_CATEGORY_KEYS.has(label) || DEBT_CATEGORY_KEYS.has(label)) return label;
  return LEGACY_EN_CASH_LABEL_TO_KEY[label] ?? LEGACY_EN_DEBT_LABEL_TO_KEY[label] ?? null;
}

function translateCategoryKey(key: string, t: TFunction): string {
  if (CASH_CATEGORY_KEYS.has(key) || key === CASHBOOK_DEBT_PAYMENT_CATEGORY) {
    return t(`cashbook.categories.${key}`);
  }
  if (DEBT_CATEGORY_KEYS.has(key)) {
    return t(`cashbook.debts.categories.${key}`);
  }
  return key;
}

/** Localize treasury row description (category keys, PayVox memos, legacy English titles). */
export function displayCashbookEntryLabel(label: string, t: TFunction, isArabic: boolean): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;

  if (isArabic && trimmed === PAYVOX_DAILY_TOTAL_MEMO_EN) {
    return t('cashbook.payvox.netSalesMemo');
  }
  if (!isArabic && trimmed === PAYVOX_DAILY_TOTAL_MEMO_AR) {
    return t('cashbook.payvox.netSalesMemo');
  }

  const key = resolveStoredCategoryKey(trimmed);
  if (key) return translateCategoryKey(key, t);

  return trimmed;
}

function isResolvableCategoryKey(key: string): boolean {
  return (
    CASH_CATEGORY_KEYS.has(key) ||
    DEBT_CATEGORY_KEYS.has(key) ||
    key === CASHBOOK_DEBT_PAYMENT_CATEGORY
  );
}

/** Localize debt ledger row description. */
export function displayCashbookDebtEntryLabel(label: string, t: TFunction): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;

  const key = resolveStoredCategoryKey(trimmed);
  if (key && isResolvableCategoryKey(key)) return translateCategoryKey(key, t);

  const sep = ' — ';
  const sepIdx = trimmed.indexOf(sep);
  if (sepIdx > 0) {
    const head = trimmed.slice(0, sepIdx);
    const tail = trimmed.slice(sepIdx + sep.length);
    const tailKey = resolveStoredCategoryKey(tail);
    if (tailKey && isResolvableCategoryKey(tailKey)) {
      return `${head}${sep}${translateCategoryKey(tailKey, t)}`;
    }
  }

  return trimmed;
}
